import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Edit, Globe, Lock, Calendar, Crown, Users, TrendingUp, Camera, Palette, ShieldCheck } from 'lucide-react';
import PermissionGate from './PermissionGate';
import AvatarSelectorModal from '../../../components/profile/AvatarSelectorModal';
import GroupBackgroundThemePicker from './GroupBackgroundThemePicker';
import { groupBackgroundStyle } from '../../../utils/groupBackgroundThemes';
import { fetchCredibilityThunk } from '../../../redux/credibility/credibilityActionThunks';
import { selectCredByUser } from '../../../redux/credibility/credibilitySlice';
import { guardrailStatus, translateGuardrailLabel } from '../../../utils/groupGuardrailTiers';

const GroupAbout = ({
  group,
  onUpdateGroup,
  isUpdating,
  isOwner,
  postsCount = null,
  avatarUrl = null,
  onSelectAvatar,
  canEditGroupTheme = false, // owner/admin/moderator -- same tier as GroupHeader's "Theme" button
  onSelectBackgroundTheme,
  isSavingTheme = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUserId = useSelector((s) => s.auth?.user?.id);
  const ownCred = useSelector((s) => (currentUserId ? selectCredByUser(s, currentUserId) : null));
  const guardrail = guardrailStatus(ownCred?.status_tier || 'Beginner', ownCred?.global_score || 0);

  useEffect(() => {
    if (isOwner && currentUserId) dispatch(fetchCredibilityThunk({ userId: currentUserId, soft: true }));
  }, [dispatch, isOwner, currentUserId]);

  const [isEditing, setIsEditing] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [editData, setEditData] = useState({
    name: group?.name || '',
    description: group?.description || '',
    bio: group?.bio || '',
    privacy: group?.privacy || 'private',
  });

  useEffect(() => {
    if (group) {
      setEditData({
        name: group.name || '',
        description: group.description || '',
        bio: group.bio || '',
        privacy: group.privacy || 'private',
      });
    }
  }, [group]);

  const handleSave = async () => {
    if (!isOwner || !onUpdateGroup) return;
    await onUpdateGroup(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      name: group?.name || '',
      description: group?.description || '',
      bio: group?.bio || '',
      privacy: group?.privacy || 'private',
    });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('groups.about.title')}</h2>
        <PermissionGate isOwner={isOwner} requireOwner>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white rounded-xl flex items-center space-x-2"
            >
              <Edit size={16} />
              <span>{t('groups.about.edit')}</span>
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl"
                disabled={isUpdating}
              >
                {t('groups.about.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={
                  isUpdating ||
                  editData.name.trim().length < 3 ||
                  editData.description.trim().length < 10 ||
                  editData.bio.trim().length < 10
                }
                title={
                  editData.name.trim().length < 3
                    ? t('groups.about.nameMinLengthError')
                    : editData.description.trim().length < 10
                    ? t('groups.about.descriptionMinLengthError')
                    : editData.bio.trim().length < 10
                    ? t('groups.about.guidelinesMinLengthError')
                    : undefined
                }
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl disabled:opacity-50"
              >
                {isUpdating ? t('groups.about.saving') : t('groups.about.saveChanges')}
              </button>
            </div>
          )}
        </PermissionGate>
      </div>

      {/* Picture/theme are their own live-saving controls (each opens its
          own modal and saves immediately via onSelectAvatar/
          onSelectBackgroundTheme) -- deliberately outside the name/
          description/bio/privacy Edit-Save-Cancel flow above, and gated by
          each action's own permission tier rather than requireOwner: the
          picture stays owner-only (matches GroupHeader.jsx's existing gate
          on the backend's admin-or-higher picture endpoint), while the
          theme is open to moderator+ (matches the mute/post permission
          tier via canEditGroupTheme). */}
      {(isOwner || canEditGroupTheme) && (
        <div className="bg-cream-100 dark:bg-white/5 border border-cream-300 dark:border-white/10 rounded-2xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Palette size={18} className="text-violet-400" />
            {t('groups.about.appearance')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {isOwner && (
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.groupPicture')}</label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={group?.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-xl font-bold">{group?.name?.charAt(0)?.toUpperCase() || 'G'}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAvatarModal(true)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-xl flex items-center gap-2 text-sm font-medium"
                  >
                    <Camera size={16} />
                    {t('groups.about.changePicture')}
                  </button>
                </div>
              </div>
            )}
            {canEditGroupTheme && (
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.pageBackgroundTheme')}</label>
                <div className="flex items-center gap-4">
                  <div
                    className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0"
                    style={groupBackgroundStyle(group?.background_theme)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowThemeModal(true)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-xl flex items-center gap-2 text-sm font-medium"
                  >
                    <Palette size={16} />
                    {t('groups.about.changeTheme')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isOwner && (
        <AvatarSelectorModal
          isOpen={showAvatarModal}
          onClose={() => setShowAvatarModal(false)}
          onSelect={(url) => {
            onSelectAvatar?.(url);
            setShowAvatarModal(false);
          }}
          currentAvatar={avatarUrl}
        />
      )}

      {canEditGroupTheme && (
        <GroupBackgroundThemePicker
          isOpen={showThemeModal}
          onClose={() => setShowThemeModal(false)}
          onSelect={(themeId) => {
            onSelectBackgroundTheme?.(themeId);
            setShowThemeModal(false);
          }}
          currentTheme={group?.background_theme}
          saving={isSavingTheme}
        />
      )}

      <div className="bg-cream-100 dark:bg-white/5 border border-cream-300 dark:border-white/10 rounded-2xl p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.groupName')}</label>
          {isEditing ? (
            <input
              type="text"
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('groups.about.groupNamePlaceholder')}
              maxLength={100}
              disabled={isUpdating}
            />
          ) : (
            <p className="text-gray-900 dark:text-white text-lg font-semibold">{group?.name || t('groups.about.unnamedGroup')}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.description')}</label>
          {isEditing ? (
            <textarea
              value={editData.description}
              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-3 rounded-xl h-32 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder={t('groups.about.descriptionPlaceholder')}
              maxLength={2000}
              disabled={isUpdating}
            />
          ) : (
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{group?.description || t('groups.about.noDescription')}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.communityGuidelines')}</label>
          {isEditing ? (
            <textarea
              value={editData.bio}
              onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
              className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-3 rounded-xl h-32 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder={t('groups.about.guidelinesPlaceholder')}
              maxLength={2000}
              disabled={isUpdating}
            />
          ) : (
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{group?.bio || t('groups.about.noGuidelines')}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.privacy')}</label>
          {isEditing ? (
            <>
              <select
                value={editData.privacy}
                onChange={(e) => setEditData({ ...editData, privacy: e.target.value })}
                className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isUpdating}
              >
                <option value="public" disabled={!guardrail.allowsPublicGroup}>
                  {t('groups.about.publicOption')}{!guardrail.allowsPublicGroup ? t('groups.about.requiresEliteTier') : ''}
                </option>
                <option value="private">{t('groups.about.privateOption')}</option>
              </select>
              {!guardrail.allowsPublicGroup && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1.5 flex items-center gap-1.5">
                  <ShieldCheck size={12} className="text-gray-500 flex-shrink-0" />
                  {t('groups.about.guardrailNote', { label: translateGuardrailLabel(guardrail.state, t), tier: ownCred?.status_tier || 'Beginner' })}
                </p>
              )}
            </>
          ) : (
            <div className="flex items-center space-x-3">
              {group?.privacy === 'public' ? <Globe className="text-green-400" size={20} /> : <Lock className="text-yellow-400" size={20} />}
              <span className="text-gray-900 dark:text-white capitalize font-medium">{group?.privacy || t('groups.about.privateFallback')}</span>
              <span className="text-gray-600 dark:text-gray-400 text-sm">- {group?.privacy === 'public' ? t('groups.about.anyoneCanJoin') : t('groups.about.inviteOnly')}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200 dark:border-gray-700/50">
          <div>
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.created')}</h4>
            <p className="text-gray-900 dark:text-white flex items-center space-x-2">
              <Calendar size={16} className="text-gray-600 dark:text-gray-400" />
              <span>{new Date(group?.created_at || Date.now()).toLocaleDateString()}</span>
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.owner')}</h4>
            <p className="text-gray-900 dark:text-white flex items-center space-x-2">
              <Crown size={16} className="text-yellow-400" />
              {/* The API returns the current owner via `members[].role === 'owner'`
                  (and `created_by_name`, which is the *original creator* -- wrong
                  after an ownership transfer). None of `owner`/`createdBy`/
                  `owner_username` exist on the real group payload, so this
                  always showed "Unknown". */}
              <span>
                {group?.members?.find((m) => m.role === 'owner')?.username ||
                  group?.created_by_name ||
                  t('groups.about.unknownOwner')}
              </span>
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.members')}</h4>
            <p className="text-gray-900 dark:text-white flex items-center space-x-2">
              <Users size={16} className="text-blue-400" />
              <span>{t('groups.about.membersCount', { count: group?.members?.length ?? '—' })}</span>
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('groups.about.activity')}</h4>
            <p className="text-gray-900 dark:text-white flex items-center space-x-2">
              <TrendingUp size={16} className="text-green-400" />
              <span>
                {postsCount === null
                  ? '—'
                  : postsCount >= 20
                  ? t('groups.activityLevels.high')
                  : postsCount >= 5
                  ? t('groups.activityLevels.moderate')
                  : postsCount >= 1
                  ? t('groups.activityLevels.low')
                  : t('groups.activityLevels.new')}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupAbout;
